<template>
  <!-- B03 — ป้ายสถานะไลบรารี่ของการบันทึก (ใช้ร่วม Host/Report — ไม่เรียก API) · eligible === false → ไม่ render (D31) -->
  <div v-if="item.eligible !== false" class="rec-badge" data-testid="rec-badge" :data-state="state">
    <v-chip
      :x-small="!small"
      :small="small"
      label
      :color="look.bg"
      :text-color="look.fg"
      class="rec-badge__chip"
      data-testid="rec-status-chip"
      :data-state="state"
    >
      <v-icon left x-small :color="look.iconColor">{{ look.icon }}</v-icon>{{ $t("livefeature_recording_status_" + state) }}
    </v-chip>
    <div v-if="reason" class="rec-badge__reason" data-testid="rec-status-reason" :data-reason="reason">{{ reasonText }}</div>
    <template v-if="state === 'in_library'">
      <div class="rec-badge__library" data-testid="rec-library-name">{{ $t("livefeature_recording_in_library_name", { name: item.topicName }) }}</div>
      <div v-if="showLessonName && item.lessonName" class="rec-badge__library" data-testid="rec-lesson-name">{{ $t("livefeature_recording_video_name", { name: item.lessonName }) }}</div>
    </template>
    <v-btn
      v-if="item.canRetry === true && canRetry"
      x-small
      outlined
      color="#16a34a"
      class="rec-badge__retry"
      :loading="retrying"
      :disabled="disabled"
      data-testid="rec-retry-btn"
      @click="$emit('retry', item)"
    >
      <v-icon left x-small>mdi-refresh</v-icon>{{ $t("livefeature_recording_retry_import") }}
    </v-btn>
  </div>
</template>

<script>
// errorCode (API) → reason (data-reason / key livefeature_recording_error_{reason}) — ห้ามแสดงรหัสดิบ (D25)
const ERROR_REASONS = {
  QUOTA_EXCEEDED: "quota",
  FILE_NOT_FOUND: "file_not_found",
  IMPORT_ERROR: "import",
  EGRESS_FAILED: "egress_failed",
  EMPTY_FILE: "empty_file",
  NO_COMPANY: "no_company",
};
// reason ที่เป็นคำอธิบาย (key livefeature_recording_{reason}) ไม่ใช่ข้อผิดพลาด
const HINT_REASONS = ["processing_hint", "auto_retrying", "removed_hint"];
// สีจาก Host/Report เดิม (E62)
const LOOKS = {
  recording: { bg: "#dbeafe", fg: "#1d4ed8", icon: "mdi-record-circle", iconColor: "#dc2626" },
  processing: { bg: "#dbeafe", fg: "#1d4ed8", icon: "mdi-progress-clock", iconColor: "#1d4ed8" },
  in_library: { bg: "#dcfce7", fg: "#166534", icon: "mdi-check-circle-outline", iconColor: "#166534" },
  removed: { bg: "#f1f5f9", fg: "#475569", icon: "mdi-delete-outline", iconColor: "#475569" },
  failed: { bg: "#fee2e2", fg: "#b91c1c", icon: "mdi-alert-circle-outline", iconColor: "#b91c1c" },
  skipped: { bg: "#f1f5f9", fg: "#475569", icon: "mdi-minus-circle-outline", iconColor: "#475569" },
};

export default {
  name: "LiveRecordingLibraryBadge",
  props: {
    item: { type: Object, required: true }, // 1 แถวจาก GetRecordingLibraryStatus
    canRetry: { type: Boolean, default: false }, // สิทธิ์ฝั่ง UI
    retrying: { type: Boolean, default: false }, // ปุ่มของแถวนี้หมุน
    disabled: { type: Boolean, default: false }, // มีการลองใหม่ค้างอยู่ทั้งหน้า
    sessionEnded: { type: Boolean, default: false }, // Report ส่ง true
    showLessonName: { type: Boolean, default: false }, // Report ส่ง true
    small: { type: Boolean, default: false }, // Report ส่ง true (chip small แทน x-small)
  },
  computed: {
    // ตาราง "สถานะ → ป้าย/เหตุผล" ของ spec ตามลำดับแถว
    status() {
      const it = this.item || {};
      const lib = it.libraryStatus;
      const errorReason = ERROR_REASONS[it.errorCode] || "unknown";
      if (it.recordStatus === 0) {
        return this.sessionEnded
          ? { state: "processing", reason: "processing_hint" }
          : { state: "recording", reason: "" };
      }
      if (it.recordStatus === 2) {
        return { state: "failed", reason: it.errorCode ? errorReason : "egress_failed" };
      }
      if (it.recordStatus === 1 && lib === 0 && it.attempts > 0) {
        return { state: "processing", reason: "auto_retrying" };
      }
      if (it.recordStatus === 1 && (lib === null || lib === undefined || lib === 0 || lib === 1)) {
        return { state: "processing", reason: "processing_hint" };
      }
      if (lib === 2) {
        return it.lessonActive === false
          ? { state: "removed", reason: "removed_hint" }
          : { state: "in_library", reason: "" };
      }
      if (lib === 3) return { state: "failed", reason: errorReason };
      if (lib === 4) return { state: "skipped", reason: errorReason };
      // ไม่อยู่ในตาราง spec (Plan C16) — ไม่ให้รหัสดิบโผล่
      return { state: "processing", reason: "processing_hint" };
    },
    state() {
      return this.status.state;
    },
    reason() {
      return this.status.reason;
    },
    reasonText() {
      if (!this.reason) return "";
      return HINT_REASONS.includes(this.reason)
        ? this.$t("livefeature_recording_" + this.reason)
        : this.$t("livefeature_recording_error_" + this.reason);
    },
    look() {
      return LOOKS[this.state] || LOOKS.processing;
    },
  },
};
</script>

<style scoped>
.rec-badge {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 100%;
  min-width: 0;
}

.rec-badge__chip {
  font-weight: 700;
  height: auto !important;
  max-width: 100%;
  min-height: 16px;
  white-space: normal;
}

/* ป้าย mm/vn ยาว → ตัดบรรทัดในชิปแทนการล้นแผง (แบบ B02 D27) */
.rec-badge__chip >>> .v-chip__content {
  line-height: 1.3;
  padding: 2px 0;
  white-space: normal;
}

.rec-badge__reason,
.rec-badge__library {
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.rec-badge__reason[data-reason="quota"],
.rec-badge__reason[data-reason="file_not_found"],
.rec-badge__reason[data-reason="import"],
.rec-badge__reason[data-reason="egress_failed"],
.rec-badge__reason[data-reason="unknown"] {
  color: #b91c1c;
}

.rec-badge__retry {
  letter-spacing: 0;
  max-width: 100%;
  text-transform: none;
}
</style>
